<template>
	<div class="activity-editor-v2">
		<el-card>
			<div slot="header" class="page-header">
				<span>{{ isEdit ? "编辑活动" : "新增活动" }}</span>
			</div>

			<el-form
				ref="activityForm"
				:model="form"
				:rules="rules"
				label-width="120px"
				class="activity-form"
			>
				<el-form-item label="活动标题" prop="title">
					<el-input v-model.trim="form.title" maxlength="200" show-word-limit />
				</el-form-item>

				<el-form-item label="活动地点" required>
					<div class="location-row">
						<el-select v-model="form.type" placeholder="请选择活动形式">
							<el-option label="线下活动" value="6" />
							<el-option label="线上活动" value="5" />
						</el-select>
						<el-form-item prop="address" class="location-input">
							<el-input
								v-model.trim="form.address"
								:placeholder="form.type === '5' ? '请输入线上活动地址或平台信息' : '例如 Singapore、Hong Kong、Hangzhou'"
							/>
						</el-form-item>
					</div>
				</el-form-item>

				<el-form-item label="活动时间" required>
					<div class="time-row">
						<el-select v-model="form.timeZone" filterable class="timezone-select">
							<el-option v-for="item in timezones" :key="item" :label="item" :value="item" />
						</el-select>
						<el-form-item prop="start_time">
							<el-date-picker
								v-model="form.start_time"
								type="datetime"
								format="yyyy-MM-dd HH:mm"
								value-format="yyyy-MM-dd HH:mm:00"
								placeholder="开始时间"
							/>
						</el-form-item>
						<span>至</span>
						<el-form-item prop="end_time">
							<el-date-picker
								v-model="form.end_time"
								type="datetime"
								format="yyyy-MM-dd HH:mm"
								value-format="yyyy-MM-dd HH:mm:00"
								placeholder="结束时间"
							/>
						</el-form-item>
					</div>
				</el-form-item>

				<el-form-item label="活动类型" prop="event_category">
					<el-radio-group v-model="form.event_category">
						<el-radio label="web3">Web3</el-radio>
						<el-radio label="ai">AI</el-radio>
					</el-radio-group>
				</el-form-item>

				<el-form-item label="参与链接" prop="url">
					<el-input v-model.trim="form.url" placeholder="请输入 http:// 或 https:// 开头的活动链接" />
				</el-form-item>

				<el-form-item label="内容封面" prop="pic">
					<div v-if="form.pic" class="cover-preview">
						<img :src="form.pic" alt="活动封面" />
						<div>
							<el-upload action="" :show-file-list="false" :http-request="uploadCover" :before-upload="beforeCoverUpload">
								<el-button size="small">重新上传</el-button>
							</el-upload>
							<el-button size="small" type="danger" plain @click="form.pic = ''">删除</el-button>
						</div>
					</div>
					<el-upload
						v-else
						action=""
						drag
						:show-file-list="false"
						:http-request="uploadCover"
						:before-upload="beforeCoverUpload"
					>
						<i class="el-icon-upload" />
						<div class="el-upload__text">点击或拖拽上传活动封面</div>
					</el-upload>
					<div class="field-tip">推荐比例 1:1，建议尺寸 800×800，支持 JPG / JPEG / PNG / WEBP，最大 10MB</div>
				</el-form-item>

				<el-form-item label="活动详情" prop="content">
					<EditorMine :content="form.content" :max-image-size-mb="5" @onEditorChange="onEditorChange" />
					<div class="field-tip">选填</div>
				</el-form-item>

				<el-form-item label="主办方">
					<div v-for="(organizer, index) in form.organizer" :key="index" class="organizer-row">
						<div class="organizer-avatar">
							<img v-if="organizer.pic" :src="organizer.pic" :alt="organizer.name || '主办方头像'" />
							<div v-else class="organizer-avatar-placeholder"><i class="el-icon-user" /></div>
							<div class="organizer-avatar-actions">
								<el-upload
									action=""
									accept="image/jpeg,image/png,image/webp"
									:show-file-list="false"
									:http-request="option => uploadOrganizerAvatar(option, index)"
									:before-upload="beforeOrganizerUpload"
								>
									<el-button type="text" :loading="Boolean(organizer.uploading)">{{ organizer.pic ? "更换头像" : "上传头像" }}</el-button>
								</el-upload>
								<el-button v-if="organizer.pic" type="text" class="danger-text" @click="organizer.pic = ''">删除</el-button>
							</div>
						</div>
						<el-input v-model.trim="organizer.name" placeholder="主办方名称" />
						<el-input v-model.trim="organizer.link" placeholder="主办方链接（选填）" />
						<el-button type="danger" plain @click="removeOrganizer(index)">删除</el-button>
					</div>
					<el-button v-if="form.organizer.length < 20" type="primary" plain icon="el-icon-plus" @click="addOrganizer">
						添加主办方
					</el-button>
					<div class="field-tip">选填，最多 20 个；头像支持 JPG / JPEG / PNG / WEBP，最大 5MB</div>
				</el-form-item>

				<el-form-item class="form-actions">
					<el-button @click="cancel">取消</el-button>
					<el-button type="primary" :loading="submitting" @click="publish">发布</el-button>
				</el-form-item>
			</el-form>
		</el-card>
	</div>
</template>

<script>
import EditorMine from "../../message/addmessage/Editor.vue";
import { uploadImage } from "@/api/user";
import { detailsactivity, saveActivityV2 } from "@/api/article";

const emptyForm = () => ({
	id: "",
	title: "",
	type: "6",
	address: "",
	timeZone: "GMT+8",
	start_time: "",
	end_time: "",
	event_category: "",
	url: "",
	pic: "",
	content: "",
	organizer: [],
});

export default {
	name: "ActivityEditorV2",
	components: { EditorMine },
	data() {
		const validUrl = (rule, value, callback) => {
			if (!/^https?:\/\/\S+$/i.test(value || "")) callback(new Error("请输入正确的活动链接"));
			else callback();
		};
		const validEndTime = (rule, value, callback) => {
			if (!value) callback(new Error("请选择结束时间"));
			else if (this.form.start_time && new Date(value) <= new Date(this.form.start_time)) callback(new Error("结束时间必须晚于开始时间"));
			else callback();
		};
		return {
			form: emptyForm(),
			submitting: false,
			saved: false,
			initialSnapshot: "",
			timezones: ["GMT-8", "GMT-5", "GMT+0", "GMT+1", "GMT+2", "GMT+3", "GMT+4", "GMT+5:30", "GMT+7", "GMT+8", "GMT+9", "GMT+10"],
			rules: {
				title: [{ required: true, message: "请输入活动标题", trigger: "blur" }],
				address: [{ required: true, message: "请输入活动地点", trigger: "blur" }],
				start_time: [{ required: true, message: "请选择开始时间", trigger: "change" }],
				end_time: [{ validator: validEndTime, trigger: "change" }],
				event_category: [{ required: true, message: "请选择活动类型", trigger: "change" }],
				url: [{ required: true, validator: validUrl, trigger: "blur" }],
				pic: [{ required: true, message: "请上传活动封面", trigger: "change" }],
			},
		};
	},
	computed: {
		isEdit() {
			return Boolean(this.$route.query.id);
		},
		hasChanges() {
			return this.initialSnapshot !== "" && JSON.stringify(this.form) !== this.initialSnapshot;
		},
	},
	created() {
		if (this.isEdit) this.loadDetail();
		else if (this.$route.query.imported === "luma") this.loadLumaImport();
		else this.setSnapshot();
		window.addEventListener("beforeunload", this.beforeUnload);
	},
	beforeDestroy() {
		window.removeEventListener("beforeunload", this.beforeUnload);
	},
	beforeRouteLeave(to, from, next) {
		if (this.saved || !this.hasChanges) return next();
		this.$confirm("当前内容尚未保存，确认离开吗？", "提示", {
			confirmButtonText: "确认离开",
			cancelButtonText: "继续编辑",
			type: "warning",
		}).then(() => next()).catch(() => next(false));
	},
	methods: {
		loadLumaImport() {
			try {
				const imported = JSON.parse(sessionStorage.getItem("event_v2_luma_import") || "null");
				if (imported) {
					this.ensureTimezoneOption(imported.timeZone);
					this.form = { ...emptyForm(), ...imported };
				}
			} finally {
				sessionStorage.removeItem("event_v2_luma_import");
				this.setSnapshot();
			}
		},
		setSnapshot() {
			this.$nextTick(() => { this.initialSnapshot = JSON.stringify(this.form); });
		},
		ensureTimezoneOption(timezone) {
			if (timezone && !this.timezones.includes(timezone)) this.timezones.push(timezone);
		},
		beforeUnload(event) {
			if (!this.saved && this.hasChanges) {
				event.preventDefault();
				event.returnValue = "";
			}
		},
		loadDetail() {
			detailsactivity({ id: this.$route.query.id }).then((res) => {
				if (res.code !== 200) return this.$message.error(res.message);
				const data = res.data || {};
				this.ensureTimezoneOption(data.timeZone);
				let organizers = [];
				try { organizers = JSON.parse(data.organizer || "[]") || []; } catch (e) { organizers = []; }
				this.form = {
					id: data.id,
					title: data.title || "",
					type: String(data.type || "6"),
					address: data.address || "",
					timeZone: data.timeZone || "GMT+8",
					start_time: data.start_time || "",
					end_time: data.end_time || "",
					event_category: data.event_category || "",
					url: data.url || "",
					pic: data.pic || "",
					content: data.content || "",
					organizer: organizers.map(item => ({
						...item,
						name: item.name || "",
						link: item.link || item.insLink || item.twLink || item.ytLink || item.tgLink || "",
						pic: item.pic || "",
						content: item.content || "",
						insLink: item.insLink || "",
						twLink: item.twLink || "",
						ytLink: item.ytLink || "",
						tgLink: item.tgLink || "",
					})),
				};
				this.setSnapshot();
			});
		},
		beforeCoverUpload(file) {
			const validType = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
			if (!validType) this.$message.error("仅支持 JPG、JPEG、PNG、WEBP 格式");
			if (file.size > 10 * 1024 * 1024) this.$message.error("活动封面不能超过 10MB");
			return validType && file.size <= 10 * 1024 * 1024;
		},
		uploadCover(option) {
			const data = new FormData();
			data.append("file", option.file);
			data.append("scene", "activity_cover");
			uploadImage(data).then((res) => {
				if (res.code === 200 && res.data && res.data.url) {
					this.form.pic = res.data.url;
					this.$refs.activityForm.validateField("pic");
				} else this.$message.error("图片上传失败，请重新上传");
			}).catch(() => this.$message.error("图片上传失败，请重新上传"));
		},
		beforeOrganizerUpload(file) {
			const validType = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
			if (!validType) this.$message.error("主办方头像仅支持 JPG、JPEG、PNG、WEBP 格式");
			if (file.size > 5 * 1024 * 1024) this.$message.error("主办方头像不能超过 5MB");
			return validType && file.size <= 5 * 1024 * 1024;
		},
		uploadOrganizerAvatar(option, index) {
			const organizer = this.form.organizer[index];
			if (!organizer) return;
			this.$set(organizer, "uploading", true);
			const data = new FormData();
			data.append("file", option.file);
			uploadImage(data).then((res) => {
				if (res.code === 200 && res.data && res.data.url) {
					this.$set(organizer, "pic", res.data.url);
				} else this.$message.error("主办方头像上传失败，请重新上传");
			}).catch(() => this.$message.error("主办方头像上传失败，请重新上传"))
				.finally(() => this.$set(organizer, "uploading", false));
		},
		onEditorChange({ html }) {
			this.form.content = html || "";
		},
		addOrganizer() {
			if (this.form.organizer.length < 20) {
				this.form.organizer.push({
					name: "", link: "", pic: "", content: "",
					insLink: "", twLink: "", ytLink: "", tgLink: "",
				});
			}
		},
		removeOrganizer(index) {
			this.form.organizer.splice(index, 1);
		},
		validateOrganizers() {
			for (let i = 0; i < this.form.organizer.length; i += 1) {
				const item = this.form.organizer[i];
				if (!item.name) { this.$message.error(`请填写第 ${i + 1} 个主办方名称`); return false; }
				if (item.link && !/^https?:\/\/\S+$/i.test(item.link)) { this.$message.error(`第 ${i + 1} 个主办方链接格式错误`); return false; }
			}
			return true;
		},
		publish() {
			this.$refs.activityForm.validate((valid, fields) => {
				if (!valid) {
					const first = Object.keys(fields || {})[0];
					const element = first && this.$el.querySelector(`[for="${first}"]`);
					if (element) element.scrollIntoView({ behavior: "smooth", block: "center" });
					return;
				}
				if (!this.validateOrganizers()) return;
				this.submitting = true;
				saveActivityV2(this.form).then((res) => {
					if (res.code === 200) {
						this.saved = true;
						this.$message.success("活动发布成功");
						this.$router.push("/event/events");
					} else this.$message.error(res.message);
				}).catch(error => this.$message.error(error.message || "发布失败")).finally(() => { this.submitting = false; });
			});
		},
		cancel() {
			this.$router.push("/event/events");
		},
	},
};
</script>

<style scoped>
.activity-editor-v2 { padding: 20px; }
.page-header { font-size: 18px; font-weight: 600; }
.activity-form { width: 920px; max-width: 100%; margin: 0 auto; }
.location-row, .time-row, .organizer-row { display: flex; gap: 12px; align-items: center; }
.location-input { flex: 1; margin-bottom: 0; }
.timezone-select { width: 150px; }
.cover-preview img { width: 480px; max-width: 100%; aspect-ratio: 1 / 1; object-fit: contain; background: #f2f4f7; display: block; margin-bottom: 12px; }
.cover-preview > div { display: flex; gap: 10px; }
.organizer-row { margin-bottom: 12px; }
.organizer-avatar { width: 84px; flex: 0 0 84px; text-align: center; }
.organizer-avatar > img, .organizer-avatar-placeholder { width: 64px; height: 64px; margin: 0 auto; border-radius: 50%; object-fit: cover; }
.organizer-avatar-placeholder { display: flex; align-items: center; justify-content: center; background: #f2f6fc; color: #909399; font-size: 28px; }
.organizer-avatar-actions { display: flex; justify-content: center; gap: 6px; white-space: nowrap; }
.organizer-avatar-actions .el-button + .el-button { margin-left: 0; }
.danger-text { color: #f56c6c; }
.field-tip { margin-top: 8px; color: #909399; font-size: 12px; }
.form-actions { margin-top: 30px; text-align: center; }
</style>
